import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import {
  ArrowRight,
  Mail,
  Sparkles,
  BookOpen,
  Zap,
  Send,
  BarChart3,
  ShieldCheck,
  User2,
  Palette,
  Layers,
} from "lucide-react";

// Public /about page — visually upgraded to match Postmoro's premium
// system. Every factual claim is grounded in the current product:
//   - Postmoro is a single-workspace AI social media platform
//   - It supports Facebook Pages, Instagram, LinkedIn, and X
//   - Multi-Business (up to 4 workspaces) and Teams (3 seats included)
//     are Business-plan features
// No invented history, funding, employee counts, or customer metrics.

const HOW_STEPS = [
  {
    n: "01",
    icon: BookOpen,
    title: "Teach Postmoro about your business",
    body: "Add your services, audience, tone, and past posts. Brand Learning studies your voice so future drafts sound like you — not a generic AI.",
  },
  {
    n: "02",
    icon: Zap,
    title: "Put AI to work",
    body: "Autopilot drafts a week of on-brand posts, images, hashtags, and CTAs against your schedule and content focus — with approval before posting by default.",
  },
  {
    n: "03",
    icon: Send,
    title: "Create and publish",
    body: "Review, edit, or approve each draft. Postmoro publishes to Facebook Pages, Instagram, LinkedIn, and X — from one calendar.",
  },
  {
    n: "04",
    icon: BarChart3,
    title: "Learn and grow",
    body: "See what published, when it ran, and refine the brief. Postmoro keeps the rhythm going while you get on with running the business.",
  },
];

const PRINCIPLES = [
  {
    icon: Zap,
    title: "Useful automation",
    body: "AI should replace the boring parts of social media, not the taste. Postmoro drafts, schedules, and repeats — you decide what actually publishes.",
  },
  {
    icon: ShieldCheck,
    title: "Human control",
    body: "Approval-before-posting is the default. Every draft is editable. Every automation has a visible off switch.",
  },
  {
    icon: Palette,
    title: "Consistent brand voice",
    body: "Brand Learning captures how your business already sounds so new posts stay on-voice week after week — no starting from a blank page.",
  },
  {
    icon: Layers,
    title: "Simpler workflows",
    body: "One workspace, one calendar, one place to publish across four networks. Multi-Business and Teams unlock when you outgrow a single workspace.",
  },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Postmoro | AI Social Media Management Platform"
        description="Learn about Postmoro, an AI-powered social media management and marketing automation platform built to help businesses create, schedule, and automate content."
        path="/about"
      />
      <div
        className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden"
        data-testid="about-page"
      >
        {/* Ambient background layers */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-0">
          <div
            className="absolute -top-40 left-1/3 h-[520px] w-[720px] rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(27,168,159,0.20), transparent 70%)" }}
          />
          <div
            className="absolute top-[380px] -left-40 h-[420px] w-[520px] rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(47,143,229,0.14), transparent 70%)" }}
          />
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.10) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage: "radial-gradient(ellipse at 30% 12%, black 30%, transparent 70%)",
              WebkitMaskImage: "radial-gradient(ellipse at 30% 12%, black 30%, transparent 70%)",
            }}
          />
        </div>

        <MarketingHeader activePath="/about" />

        {/* ============ HERO ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-8 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
              About Postmoro
            </div>
            <h1
              className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]"
              data-testid="about-hero-title"
            >
              Social media that keeps up with the business.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
              Postmoro is an AI-powered social media management and marketing
              automation platform. It helps businesses <strong className="text-[color:var(--text-1)]">create</strong>,{" "}
              <strong className="text-[color:var(--text-1)]">automate</strong>,{" "}
              <strong className="text-[color:var(--text-1)]">learn</strong>, and{" "}
              <strong className="text-[color:var(--text-1)]">grow</strong> from a single
              workspace — across Facebook, Instagram, LinkedIn, and X.
            </p>
          </div>
        </section>

        {/* ============ MISSION / APPROACH ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-10 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className="relative rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6 overflow-hidden"
              data-testid="about-mission"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full"
                style={{ background: "radial-gradient(closest-side, rgba(27,168,159,0.16), transparent 70%)" }}
              />
              <div className="relative flex items-center gap-2">
                <div className="h-8 w-8 rounded-[10px] bg-[color:rgba(27,168,159,0.12)] border border-[color:rgba(27,168,159,0.40)] text-[color:var(--brand-primary)] flex items-center justify-center">
                  <User2 className="h-4 w-4" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">
                  Who Postmoro is for
                </div>
              </div>
              <h2 className="relative mt-3 font-display text-xl font-semibold">
                Small business owners who want calm, consistent social
              </h2>
              <p className="relative mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
                Postmoro is built for small business owners, entrepreneurs,
                agencies, and teams who want their social media to feel calm
                and consistent — without spending the week staring at a blank
                text box. The AI drafts each post, learns your voice over
                time, and lines the week up on one calendar.
              </p>
            </div>

            <div
              className="relative rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6 overflow-hidden"
              data-testid="about-approach"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full"
                style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.14), transparent 70%)" }}
              />
              <div className="relative flex items-center gap-2">
                <div className="h-8 w-8 rounded-[10px] bg-[color:rgba(34,211,238,0.10)] border border-[color:rgba(34,211,238,0.40)] text-[color:var(--brand-secondary)] flex items-center justify-center">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-secondary)]">
                  How we approach AI
                </div>
              </div>
              <h2 className="relative mt-3 font-display text-xl font-semibold">
                AI drafts. Humans decide.
              </h2>
              <p className="relative mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
                Every Postmoro draft is editable. By default, Autopilot lands
                new posts in an approval queue so you review before anything
                publishes. When you're ready to hand over more of the driving,
                one toggle switches Autopilot to fully automatic — you decide
                the pace.
              </p>
            </div>
          </div>
        </section>

        {/* ============ HOW POSTMORO THINKS — 4-step story ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-12 relative">
          <p className="eyebrow text-[color:var(--brand-primary)]">How Postmoro thinks</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight max-w-2xl">
            One workspace, four moves — repeated every week.
          </h2>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_STEPS.map((s, i) => (
              <div
                key={s.n}
                className="relative rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 overflow-hidden"
                data-testid={`about-step-${s.n}`}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-14 -right-14 h-32 w-32 rounded-full"
                  style={{
                    background:
                      i === 1 || i === 3
                        ? "radial-gradient(closest-side, rgba(34,211,238,0.14), transparent 70%)"
                        : "radial-gradient(closest-side, rgba(27,168,159,0.16), transparent 70%)",
                  }}
                />
                <div className="relative flex items-center justify-between">
                  <div className="h-9 w-9 rounded-[10px] bg-[color:rgba(27,168,159,0.12)] border border-[color:rgba(27,168,159,0.40)] text-[color:var(--brand-primary)] flex items-center justify-center">
                    <s.icon className="h-4 w-4" />
                  </div>
                  <div className="font-mono-tabular text-xs text-[color:var(--text-3)] tracking-widest">
                    {s.n}
                  </div>
                </div>
                <h3 className="relative mt-4 font-display text-base font-semibold leading-snug">
                  {s.title}
                </h3>
                <p className="relative mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ============ PRODUCT PRINCIPLES ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-12 relative">
          <p className="eyebrow text-[color:var(--brand-primary)]">Product principles</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight max-w-2xl">
            The rules Postmoro is built on
          </h2>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {PRINCIPLES.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 flex items-start gap-4"
                data-testid={`about-principle-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="h-10 w-10 rounded-[10px] bg-[color:rgba(27,168,159,0.10)] border border-[color:rgba(27,168,159,0.40)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
                  <p.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-base font-semibold">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-[color:var(--text-2)] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ WHAT POSTMORO DOES (product visuals) ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-10 relative">
          <p className="eyebrow text-[color:var(--brand-primary)]">What Postmoro does</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight max-w-2xl">
            A social media platform, an AI writer, and a scheduler — together
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-[color:var(--text-2)]">
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">AI content</div>
              <p className="mt-2 leading-relaxed">
                Postmoro drafts captions, hashtags, and images so you're never
                facing a blank screen. Every piece is editable before it goes live.
              </p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                Scheduling &amp; Autopilot
              </div>
              <p className="mt-2 leading-relaxed">
                Plan a whole week at once with Autopilot, or write a one-off
                post manually. Postmoro publishes to Facebook Pages, Instagram,
                LinkedIn, and X.
              </p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                Multi-Business &amp; Teams
              </div>
              <p className="mt-2 leading-relaxed">
                Business subscribers can manage up to four separate business
                workspaces and invite team members with per-workspace roles.
              </p>
            </div>
          </div>
        </section>

        {/* ============ COMPANY (unchanged data) ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div
            className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6 sm:p-8"
            data-testid="about-company-info"
          >
            <h2 className="font-display text-xl font-semibold">Company</h2>
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
              <div>
                <dt className="text-[color:var(--text-3)]">Brand</dt>
                <dd className="text-[color:var(--text-1)]">Postmoro</dd>
              </div>
              <div>
                <dt className="text-[color:var(--text-3)]">Legal entity</dt>
                <dd className="text-[color:var(--text-1)]">Postmoro LLC</dd>
              </div>
              <div>
                <dt className="text-[color:var(--text-3)]">Website</dt>
                <dd>
                  <a
                    className="text-[color:var(--brand-primary)] hover:underline"
                    href="https://postmoro.com/"
                  >
                    postmoro.com
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-[color:var(--text-3)]">Support</dt>
                <dd>
                  <a
                    className="text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-1"
                    href="mailto:support@postmoro.com"
                  >
                    <Mail className="h-3.5 w-3.5" /> support@postmoro.com
                  </a>
                </dd>
              </div>
            </dl>
            <div className="mt-6 flex flex-wrap gap-3 text-xs text-[color:var(--text-3)]">
              <a
                href="https://www.linkedin.com/company/postmoro"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
                data-testid="about-social-linkedin"
              >
                LinkedIn
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://x.com/Postmoro"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
                data-testid="about-social-x"
              >
                X
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://www.facebook.com/profile.php?id=61590293413413"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
                data-testid="about-social-facebook"
              >
                Facebook
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://www.instagram.com/postmoroofficial"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
                data-testid="about-social-instagram"
              >
                Instagram
              </a>
            </div>
          </div>
        </section>

        {/* ============ CLOSING CTA ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="relative rounded-2xl border border-[color:rgba(27,168,159,0.35)] bg-[color:var(--surface-1)] p-8 sm:p-10 text-center overflow-hidden">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 0%, rgba(27,168,159,0.20), transparent 60%), radial-gradient(ellipse at 100% 100%, rgba(47,143,229,0.16), transparent 60%)",
              }}
            />
            <div className="relative">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Ready to try Postmoro?
              </h2>
              <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">
                Start free, connect a network, generate your first post. No credit card required.
              </p>
              <div className="mt-6">
                <Link to="/login">
                  <Button
                    size="lg"
                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                    data-testid="about-closing-cta"
                  >
                    Start free <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
